import React, { useState, useEffect, useMemo } from "react";
import { Col, Modal, Spinner } from "react-bootstrap";
import Button from "../../components/shared/buttons/button";
import { CancelSvg } from "../../svgFiles/CancelSvg";
import SelectBox from "../../components/shared/fields/SelectBox ";
import { useUpdateTimezoneMutation } from "../auth/authApi";
import { useSelector, useDispatch } from "react-redux";
import { setUserTimezone } from "../../app/globalSlice";
import { showToast } from "../../helpers/Utils";
import useTranslator from "../../hooks/useTranslator";
import { normalizeTimezone } from "../../helpers/timezoneUtils";

const FULL_TIMEZONE_OPTIONS = [
  // United States
  { label: "US - Eastern (New York)", value: "America/New_York" },
  { label: "US - Central (Chicago)", value: "America/Chicago" },
  { label: "US - Mountain (Denver)", value: "America/Denver" },
  { label: "US - Pacific (Los Angeles)", value: "America/Los_Angeles" },
  { label: "US - Hawaii (Honolulu)", value: "Pacific/Honolulu" },
  // Mexico
  { label: "Mexico - Central (Mexico City)", value: "America/Mexico_City" },
  { label: "Mexico - Mountain (Mazatlan)", value: "America/Mazatlan" },
  { label: "Mexico - Eastern (Cancun)", value: "America/Cancun" },
  // India
  { label: "India - Standard Time (Kolkata)", value: "Asia/Kolkata" },
];

const LIMITED_TIMEZONE_OPTIONS = [
  { label: "MST", value: "America/Mazatlan" },
  { label: "PST", value: "America/Los_Angeles" },
  { label: "IST", value: "Asia/Kolkata" },
];

const isLocalEnvironment = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".local")
  );
};

const TimezoneModal = ({ show, setShow }) => {
  const { _t } = useTranslator();
  const dispatch = useDispatch();
  const userTimezone = useSelector((state) => state.global.userTimezone);
  const [selectedTimezone, setSelectedTimezone] = useState(
    normalizeTimezone(userTimezone) || ""
  );
  const [updateTimezone, { isLoading }] = useUpdateTimezoneMutation();
  const [isUnreliableTimezone, setIsUnreliableTimezone] = useState(false);
  const timezoneOptions = useMemo(
    () => (isLocalEnvironment() ? FULL_TIMEZONE_OPTIONS : LIMITED_TIMEZONE_OPTIONS),
    []
  );

  // Check if browser timezone is unreliable
  useEffect(() => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const unreliable = browserTimezone === "Etc/Greenwich" || (browserTimezone === "UTC" && !userTimezone);
    setIsUnreliableTimezone(unreliable);
  }, [userTimezone]);

  useEffect(() => {
    if (!show) {
      return;
    }

    const normalizedUserTimezone = normalizeTimezone(userTimezone);

    if (
      normalizedUserTimezone &&
      timezoneOptions.some((option) => option.value === normalizedUserTimezone)
    ) {
      setSelectedTimezone(normalizedUserTimezone);
      return;
    }

    setSelectedTimezone((currentSelectedTimezone) => {
      if (
        timezoneOptions.some((option) => option.value === currentSelectedTimezone)
      ) {
        return currentSelectedTimezone;
      }

      return timezoneOptions[0]?.value || "";
    });
  }, [show, userTimezone, timezoneOptions]);

  const handleClose = () => {
    setShow(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTimezone) {
      showToast(
        _t("please_select_timezone", "Please select a timezone."),
        "error"
      );
      return;
    }

    try {
      const normalizedTimezone = normalizeTimezone(selectedTimezone);

      await updateTimezone({
        timezone: normalizedTimezone,
      }).unwrap();

      // Update Redux store
      dispatch(setUserTimezone(normalizedTimezone));
      localStorage.setItem("userTimezone", normalizedTimezone);

      showToast(
        _t("timezone_updated_successfully", "Timezone updated successfully"),
        "success"
      );
      handleClose();
      // Hard refresh to ensure all screens recalculate dates/times with updated timezone.
      window.location.replace(window.location.href);
    } catch (error) {
      showToast(
        error?.data?.message || _t("failed_to_update_timezone", "Failed to update timezone"),
        "error"
      );
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Header className="border-bottom-0 d-flex justify-content-between align-items-center">
        <h3 className="m-0">{_t("change_timezone", "Change Timezone")}</h3>
        <Button onClick={handleClose} icon={CancelSvg} size="small"></Button>
      </Modal.Header>
      <Modal.Body>
        {isUnreliableTimezone && (
          <Col lg={12} className="mb-3 p-3" style={{ backgroundColor: "#fff3cd", borderRadius: "4px", border: "1px solid #ffc107" }}>
            <p className="mb-0" style={{ color: "#856404", fontSize: "14px" }}>
              <strong>⚠️ {_t("timezone_warning", "Important:")}</strong> Your browser's timezone detection appears to be unreliable. 
              Please manually select your correct timezone below to ensure accurate time tracking.
            </p>
          </Col>
        )}
        
        <Col lg={12} className="mb-3">
          <p className="text-muted">
            {_t(
              "selected_timezone_desc",
              "Your current timezone is used for all time-based operations in the system."
            )}
          </p>
        </Col>
        <Col lg={12}>
          <SelectBox
            label={_t("timezone", "Timezone")}
            name="timezone"
            options={timezoneOptions}
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(e.target.value)}
          />
        </Col>
        <Col lg={12} className="mt-4">
          <Button
            className="w-100"
            label={
              isLoading
                ? `${_t("updating", "Updating")}...`
                : _t("confirm", "Confirm")
            }
            onClick={handleSubmit}
            disabled={isLoading}
          />
        </Col>
      </Modal.Body>
    </Modal>
  );
};

export default TimezoneModal;
